import { CountryCode } from '@magicdoorinc/ui';
import type { IAddress } from '@magicdoorinc/ui';

declare global {
  interface Window {
    google: any;
  }
}

export type PlacePrediction = {
  id: string;
  description: string;
  main_text: string;
  secondary_text: string;
};

const FALLBACK_GOOGLE_MAPS_API_KEY = 'AIzaSyB89updWgLCG03Q81M-bZm0ESgVpSyCZ0I';

export const getGoogleMapsApiKey = () => {
  return (import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string | undefined) || FALLBACK_GOOGLE_MAPS_API_KEY;
};

export const getGoogleMapsScriptSrc = (libraries = 'places') => {
  return `https://maps.googleapis.com/maps/api/js?key=${getGoogleMapsApiKey()}&libraries=${libraries}&language=en`;
};

let isGoogleScriptLoaded = false;
let autocompleteService: any;
let placesService: any;
let sessionToken: any;

const renewSessionToken = () => {
  if (!window.google?.maps?.places) {
    return;
  }
  sessionToken = new window.google.maps.places.AutocompleteSessionToken();
};

const loadGoogleMapsScript = () => {
  if (window.google?.maps?.places) {
    isGoogleScriptLoaded = true;
    return Promise.resolve();
  }

  const existing = document.querySelector<HTMLScriptElement>(`script[src^="https://maps.googleapis.com/maps/api/js"]`);
  if (existing) {
    return new Promise<void>((resolve, reject) => {
      if (window.google?.maps?.places) {
        isGoogleScriptLoaded = true;
        resolve();
        return;
      }
      existing.addEventListener('load', () => {
        isGoogleScriptLoaded = true;
        resolve();
      });
      existing.addEventListener('error', () => reject(new Error('Failed to load Google Maps API')));
    });
  }

  return new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = getGoogleMapsScriptSrc();
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);

    script.onload = () => {
      isGoogleScriptLoaded = true;
      resolve();
    };
    script.onerror = () => {
      reject(new Error('Failed to load Google Maps API'));
    };
  });
};

export const ensureGooglePlaces = async () => {
  if (!isGoogleScriptLoaded) {
    await loadGoogleMapsScript();
  }
  autocompleteService = autocompleteService || new window.google.maps.places.AutocompleteService();
  if (!sessionToken) {
    renewSessionToken();
  }

  if (!placesService) {
    const div = document.createElement('div');
    placesService = new window.google.maps.places.PlacesService(div);
  }
};

const getPlaceDetails = (placeId: string, token: any): Promise<any> => {
  return new Promise((resolve, reject) => {
    placesService.getDetails(
      {
        placeId,
        fields: ['address_components', 'formatted_address'],
        sessionToken: token,
      },
      (place: any, status: any) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK) {
          resolve(place);
        } else {
          reject(new Error(`Place details request failed: ${status}`));
        }
      }
    );
  });
};

const mapCountryToFormCode = (longName?: string, shortName?: string): string => {
  if (shortName === 'US' || longName === 'United States' || shortName === 'USA' || longName === 'USA') {
    return CountryCode.USA;
  }
  if (shortName === 'CA' || longName === 'Canada' || shortName === 'CAN' || longName === 'CAN') {
    return CountryCode.CAN;
  }
  return CountryCode.USA;
};

export const parseAddressComponents = (addressComponents: any[]): IAddress => {
  const longNames: Record<string, string> = {};
  const shortNames: Record<string, string> = {};

  addressComponents.forEach((component: any) => {
    const types = component.types || [];
    types.forEach((type: string) => {
      if (component.long_name && !longNames[type]) {
        longNames[type] = component.long_name;
      }
      if (component.short_name && !shortNames[type]) {
        shortNames[type] = component.short_name;
      }
    });
  });

  const streetNumber = longNames.street_number || '';
  const route = longNames.route || '';

  return {
    streetAddress1: [streetNumber, route].filter(Boolean).join(' '),
    streetAddress2: longNames.subpremise || '',
    city: longNames.locality || longNames.sublocality || longNames.administrative_area_level_3 || '',
    state: shortNames.administrative_area_level_1 || '',
    zipCode: longNames.postal_code || '',
    country: mapCountryToFormCode(longNames.country, shortNames.country),
  };
};

export const fetchPlacePredictions = async (query: string | undefined): Promise<PlacePrediction[]> => {
  const q = (query || '').trim();
  if (!q || q.length < 2) {
    return [];
  }

  try {
    await ensureGooglePlaces();
  } catch (e) {
    console.error(e);
    return [];
  }

  return new Promise((resolve) => {
    autocompleteService.getPlacePredictions({ input: q, sessionToken }, (predictions: any[], status: any) => {
      if (!predictions || status !== window.google.maps.places.PlacesServiceStatus.OK) {
        resolve([]);
        return;
      }
      resolve(
        predictions.map((p: any) => ({
          id: p.place_id,
          description: p.description,
          main_text: p.structured_formatting?.main_text,
          secondary_text: p.structured_formatting?.secondary_text,
        }))
      );
    });
  });
};

export const fetchPlaceAddress = async (placeId: string): Promise<IAddress | null> => {
  try {
    await ensureGooglePlaces();
    const tokenForDetails = sessionToken;
    renewSessionToken();
    const place = await getPlaceDetails(placeId, tokenForDetails);
    if (place?.address_components) {
      return parseAddressComponents(place.address_components);
    }
    return null;
  } catch (error) {
    console.error('Error fetching place details:', error);
    return null;
  }
};
